import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CommandDialog, CommandInput, CommandList, CommandEmpty,
  CommandGroup, CommandItem, CommandSeparator,
} from '@/components/ui/command';
import { LayoutDashboard, Network, Truck, BarChart3, Calendar, FileText, Database, Package, Users, ScanSearch, Sparkles } from 'lucide-react';
import { orders, trucks, supplyChainNodes } from '@/data/mockData';
import { t } from '@/i18n';
import { AssistantPanel } from './AssistantPanel';

const pages = [
  { label: t('nav.trucks'), href: '/kamiony', icon: Truck },
  { label: t('nav.stats'), href: '/statistiky', icon: BarChart3 },
  { label: t('nav.dashboard'), href: '/dashboard', icon: LayoutDashboard },
  { label: t('nav.supplyChain'), href: '/supply-chain', icon: Network },
  { label: t('nav.planning'), href: '/planovani', icon: Calendar },
  { label: t('nav.orders'), href: '/objednavky', icon: FileText },
  { label: t('nav.sapIntegration'), href: '/sap', icon: Database },
  { label: t('nav.branchDetection'), href: '/detekce-vetvi', icon: ScanSearch },
];

export const CommandPalette = ({ open, onOpenChange }) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [asked, setAsked] = useState(null); // question sent to the assistant (#76)

  // A fresh palette each time it opens.
  useEffect(() => { if (!open) { setQuery(''); setAsked(null); } }, [open]);

  // Register Cmd+K globally
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        onOpenChange(true);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onOpenChange]);

  const go = (path) => {
    onOpenChange(false);
    navigate(path);
  };

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput placeholder={t('cmd.placeholder')} value={query} onValueChange={setQuery} />
      {asked ? (
        <AssistantPanel query={asked} pages={pages} onGo={go} onBack={() => setAsked(null)} />
      ) : (
      <CommandList>
        <CommandEmpty>{t('cmd.noResults')}</CommandEmpty>

        {query.trim().length >= 2 && (
          <CommandGroup heading={t('ai.assistant.title')} forceMount>
            <CommandItem value={`ai-ask ${query}`} forceMount onSelect={() => setAsked(query.trim())}>
              <Sparkles className="mr-2 h-4 w-4 text-primary" />
              <span>{t('ai.assistant.ask')}: „{query.trim()}“</span>
            </CommandItem>
          </CommandGroup>
        )}

        <CommandGroup heading={t('cmd.pages')}>
          {pages.map(p => (
            <CommandItem key={p.href} onSelect={() => go(p.href)}>
              <p.icon className="mr-2 h-4 w-4" />
              <span>{p.label}</span>
            </CommandItem>
          ))}
        </CommandGroup>

        <CommandSeparator />

        <CommandGroup heading={t('cmd.orders')}>
          {orders.slice(0, 8).map(o => (
            <CommandItem key={o.id} onSelect={() => go(`/objednavky?highlight=${o.id}`)}>
              <FileText className="mr-2 h-4 w-4" />
              <span className="font-mono text-sm">{o.id}</span>
              <span className="ml-2 text-muted-foreground text-xs">{o.customer?.name}</span>
            </CommandItem>
          ))}
        </CommandGroup>

        <CommandSeparator />

        <CommandGroup heading={t('cmd.trucks')}>
          {trucks.slice(0, 8).map(tr => (
            <CommandItem key={tr.id} onSelect={() => go(`/kamiony?highlight=${tr.id}`)}>
              <Truck className="mr-2 h-4 w-4" />
              <span className="font-semibold">{tr.plate}</span>
              <span className="ml-2 text-muted-foreground text-xs">{tr.driver?.name} · {tr.company}</span>
            </CommandItem>
          ))}
        </CommandGroup>

        <CommandSeparator />

        <CommandGroup heading={t('cmd.supplyChainNodes')}>
          {supplyChainNodes.map(n => (
            <CommandItem key={n.id} onSelect={() => go(`/supply-chain?highlight=${n.id}`)}>
              <Network className="mr-2 h-4 w-4" />
              <span>{n.label}</span>
              <span className="ml-2 text-muted-foreground text-xs">{n.utilization}% {t('cmd.util')}</span>
            </CommandItem>
          ))}
        </CommandGroup>
      </CommandList>
      )}
    </CommandDialog>
  );
};
